import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { AckHealthEventRequest, HealthEventDto, HealthEventsResponse } from '../api/contracts';

export function healthEventsQueryKey(unacknowledged: boolean) {
  return ['health-events', { unacknowledged }] as const;
}

/**
 * GET /api/health-events?unacknowledged=true|false
 */
export function useHealthEvents(unacknowledged: boolean) {
  return useQuery({
    queryKey: healthEventsQueryKey(unacknowledged),
    queryFn: () =>
      apiClient.get<HealthEventsResponse>(
        `/api/health-events?unacknowledged=${unacknowledged ? 'true' : 'false'}`,
      ),
  });
}

/**
 * POST /api/health-events/ack
 *
 * The route's DynamoDB key is rebuilt from BOTH `ts` and `id` (see
 * contracts.ts's comment on AckHealthEventRequest / keys.ts's
 * healthEventKey(ts, id)) -- sending `id` alone is not enough for the
 * backend to find the item. Exported standalone (not inlined in the
 * mutationFn closure) so a test can call it directly against a mocked
 * apiClient without needing to render the hook through React.
 */
export function ackHealthEvent(body: AckHealthEventRequest): Promise<void> {
  return apiClient.post<void>('/api/health-events/ack', body);
}

/** Build the exact wire body for a given row -- keeps the {ts, id} pairing in one place. */
export function toAckRequest(event: Pick<HealthEventDto, 'ts' | 'id'>): AckHealthEventRequest {
  return { ts: event.ts, id: event.id };
}

/** Maps a HealthEventDto's severity to a Cloudscape StatusIndicator `type`. */
export function severityIndicatorType(
  severity: HealthEventDto['severity'],
): 'error' | 'warning' | 'info' {
  switch (severity) {
    case 'error':
      return 'error';
    case 'warn':
      return 'warning';
    case 'info':
      return 'info';
  }
}

export function useAckHealthEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (event: Pick<HealthEventDto, 'ts' | 'id'>) => ackHealthEvent(toAckRequest(event)),
    onSuccess: () => {
      // Both the unacknowledged and "show all" views may be mounted/cached;
      // invalidate every health-events query regardless of the unacknowledged param.
      void queryClient.invalidateQueries({ queryKey: ['health-events'] });
    },
  });
}
